import { cloneDeep, isEqual } from 'lodash';
import { useRouter } from 'next/router';
import { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import StableStringify from 'json-stable-stringify';
import MurmurHash from 'imurmurhash';
import { toast } from 'react-toastify';
import { getOfflinePagesBySpace, removeOfflinePage } from '@typedream/data';
import { TDescendant, TElement, usePlateActions, usePlateSelectors } from '@udecode/plate-core';
import { pruneUnusedCollectionData } from '@/utils/collection';
import { BLOCK_TYPE } from '@/sharedComponents/TextEditor/types';
import { Navbar2Element } from '@typedream/core';
import { SAVE_INTERVAL, BACKUP_INTERVAL, LAST_BACKEDUP_DATA } from '../../constants';
import { initialValueEmpty } from '../../sharedComponents/TextEditor';
import { isSaveHotkey } from '../../sharedComponents/TextEditor/commands/hotkeys';
import {
  NavbarEditorContext,
  NavbarEditorDispatchContext,
  NavbarEditorProvider,
} from '../../sharedComponents/TextEditor/context/NavbarEditorContextProvider';
import { TextEditorContext } from '../../sharedComponents/TextEditor/context/TextEditorContext';
import useInterval from '../../utils/useInterval';
import { errToString } from '../../utils/errors';
import UnsavedPagesPopup from '../../components/UnsavedPagesPopup';
import { UnsavedPagesContext } from '../../context/UnsavedPagesContext';
import { CollectionContext } from '../collection/context';
import { ICustomizationUpdateRequest } from '../../types';
import { EditorEditableContext } from '../../sharedComponents/TextEditor/context/EditorEditableContextProvider';
import isContentEqualNoID from '../../sharedComponents/TextEditor/utils/isContentEqualNoID';
import { useSpaceAndPageIDFromRouter } from '../space/hooks';
import {
  useGetPageQuery,
  useSaveContentMutation,
  useUpdatePageCustomizationMutation,
  useGetSpaceQuery,
  useSaveNavbarMutation,
  useUpdateSpaceCustomizationPartialMutation,
} from '../../store';
import { DreamEditor, DreamValue } from '../../sharedComponents/TextEditor/plate';
import { api } from '../api';
import isNavbarEqual from '../../sharedComponents/TextEditor/utils/isNavbarEqual';
import DreamProvider from '../../sharedComponents/TextEditor/provider';
import isTransclusionEqual from '../../utils/isTransclusionEqual';

function isBlockTypeOf<T extends TElement>(element: TDescendant, type: string): element is T {
  return element.type === type;
}

export function BaseTextEditorProvider({
  children,
  editorID,
}: {
  children: ReactNode;
  editorID: string;
}) {
  const editable = useContext(EditorEditableContext);
  const router = useRouter();
  const [valuePageID, setvaluePageID] = useState(null);
  const [initialValue, setInitialValueOriginal] = useState(initialValueEmpty as DreamValue);

  const setInitialValue = (value: DreamValue) => {
    if (value.length > 0) {
      const firstChild = value[0].children[0];
      if (isBlockTypeOf<Navbar2Element>(firstChild, 'navbar')) {
        if (firstChild.children.length === 0) {
          const deepClone = cloneDeep(value);
          firstChild.children.push({ text: '' });
          deepClone[0].children[0] = firstChild;
          setInitialValueOriginal(deepClone);
          return;
        }
      }
    }
    setInitialValueOriginal(value);
  };

  const setValue = usePlateActions<DreamValue, DreamEditor>(editorID).value();
  const navbar = useContext(NavbarEditorContext);
  const setNavbar = useContext(NavbarEditorDispatchContext);
  const [loading, setloading] = useState(true);
  const loadingRef = useRef(true);
  const [unsavedPages, setunsavedPages] = useState(null);
  const [openUnsavedPages, setopenUnsavedPages] = useState(false);
  const { slug } = router.query;

  const currEditor = usePlateSelectors<DreamValue, DreamEditor>(editorID).editor();
  const getEditor = useCallback(() => currEditor, [currEditor]);
  const { schema } = useContext(CollectionContext); // optional

  const schemaRef = useRef(schema);
  const schemaOrderRef = useRef(schema ? Object.keys(schema) : []);
  useEffect(() => {
    schemaRef.current = schema;
    schemaOrderRef.current = schema ? Object.keys(schema) : [];
  }, [schema]);

  const [spaceID, pageID] = useSpaceAndPageIDFromRouter();

  const { data: space, error: errSpace } = useGetSpaceQuery([spaceID]);
  const { data: page } = useGetPageQuery([spaceID, pageID]);

  const { mutate: saveNavbar, error: errorSaveNavbar } = useSaveNavbarMutation({
    fixedCacheKey: `saveNavbar-${spaceID}`,
  });
  const { mutate: saveContent, isError: isErrorSaveContent } = useSaveContentMutation({
    fixedCacheKey: `saveContent-${pageID}`,
  });

  const getCurrentSpace = useCallback(() => useGetSpaceQuery.get(spaceID)?.data, [spaceID]);

  const getCurrentSpaceIsLoading = useCallback(
    () => useGetSpaceQuery.get(spaceID)?.isLoading,
    [spaceID]
  );

  const getCurrentPage = useCallback(
    () => useGetPageQuery.get(spaceID, pageID)?.data,
    [pageID, spaceID]
  );

  const getCurrentPageIsLoading = useCallback(
    () => !!useGetPageQuery.get(spaceID, pageID)?.isLoading,
    [pageID, spaceID]
  );

  const currVal = usePlateSelectors<DreamValue, DreamEditor>(editorID).value();
  const getCurrentValue = useCallback(() => currVal, [currVal]);

  const isNavbarChanged = useMemo(
    () => !isNavbarEqual(space?.navbar, navbar),
    [space?.navbar, navbar]
  );

  const editorValue = usePlateSelectors<DreamValue, DreamEditor>(editorID).value();
  const savedValue = page?.content;
  const isContentChanged = !isContentEqualNoID(savedValue, editorValue);
  const [isTransclusionChanged, setIsTransclusionChanged] = useState(false);

  const savedTransclusions = space?.customization?.transclusion_reference;

  useEffect(() => {
    if (space?.customization?.transclusion_reference) {
      getEditor()?.store.getState().setTransclusions(space?.customization?.transclusion_reference);
    }
    if (space?.customization?.form_reference) {
      getEditor()?.store.getState().setFormReferences(space?.customization?.form_reference);
    }
    if (space?.customization?.collection_item_data) {
      getEditor()
        ?.store.getState()
        .setSpaceCollectionItemData(space?.customization?.collection_item_data);
    }
    if (space?.routes) {
      getEditor()?.store.getState().setSpaceRoutes(space?.routes);
    }
  }, [space]);

  useEffect(() => {
    if (page?.customization?.collection_data) {
      getEditor()?.store.getState().setPageCollectionData(page?.customization?.collection_data);
    }
  }, [page]);

  useEffect(() => {
    const currTransclusion = currEditor?.store?.getState?.().transclusions;
    if (currTransclusion && savedTransclusions) {
      setIsTransclusionChanged(!isTransclusionEqual(currTransclusion, savedTransclusions));
    }

    return currEditor.store.subscribe((state) => {
      const { transclusions: currentTransclusions } = state;
      if (savedTransclusions && currentTransclusions) {
        setIsTransclusionChanged(!isTransclusionEqual(currentTransclusions, savedTransclusions));
      }
    });
  }, [currEditor, savedTransclusions]);

  const isDraft = isNavbarChanged || isContentChanged || isTransclusionChanged;

  const getLastBackupHash = (currSlug: string, spaceId: string, pageId: string) => {
    const lastBackup = localStorage.getItem(LAST_BACKEDUP_DATA);
    const parsedBackup = JSON.parse(lastBackup);
    return parsedBackup?.[currSlug]?.[spaceId]?.[pageId];
  };

  const saveLastBackupHashLocally = (
    currSlug: string,
    spaceId: string,
    pageId: string,
    contentHash: string
  ) => {
    const lastBackup = localStorage.getItem(LAST_BACKEDUP_DATA);
    const parsedBackup = JSON.parse(lastBackup);
    const newBackupLocal = {
      ...parsedBackup,
      [currSlug]: {
        ...parsedBackup?.[currSlug],
        [spaceId]: {
          ...parsedBackup?.[currSlug]?.[spaceId],
          [pageId]: contentHash,
        },
      },
    };
    localStorage.setItem(LAST_BACKEDUP_DATA, JSON.stringify(newBackupLocal));
  };

  const saveBackup = useCallback(async () => {
    if (!editable) return;
    const valueToBeSaved = StableStringify(getCurrentValue());
    const valueToBeSavedHash = MurmurHash(valueToBeSaved).result().toString();
    const lastBackupHash = getLastBackupHash(slug as string, spaceID, pageID);
    if (valueToBeSavedHash !== lastBackupHash && space) {
      api()
        .history()
        .backup(spaceID, pageID, valueToBeSaved)
        .then(() => saveLastBackupHashLocally(space.slug, spaceID, pageID, valueToBeSavedHash))
        .catch(() => null);
    }
  }, [editable, getCurrentValue, slug, spaceID, pageID, space]);

  // Handle backup every BACKUP_INTERVAL
  useInterval(saveBackup, BACKUP_INTERVAL * 1000);

  const checkUnsavedPages = useCallback(
    async (_spaceID: string, _pageID: string, responseContentUpdatedAt: string) => {
      // Check if any page in this space has local unsaved changes
      // from a previous session
      const spaceUnsavedPages = await getOfflinePagesBySpace(_spaceID);
      setunsavedPages(spaceUnsavedPages?.length > 0 ? spaceUnsavedPages : null);

      if (spaceUnsavedPages && spaceUnsavedPages?.length > 0) {
        // Check if there are unsaved changes for the current page
        const currentPageUnsaved = spaceUnsavedPages.find((p) => p.id === _pageID);
        // if there are unsaved changes for the current page
        if (currentPageUnsaved) {
          const contentUpdatedAt = new Date(responseContentUpdatedAt).getTime();
          // and there are more recent updates to this page
          if (
            responseContentUpdatedAt &&
            new Date(currentPageUnsaved.content_updated_at).getTime() < contentUpdatedAt
          ) {
            // then ask if they want to restore the unsaved change
            setopenUnsavedPages(true);
          } else {
            // otherwise, if there's no more recent updates to this page
            // then apply the unsaved change
            setInitialValue(currentPageUnsaved.content);
            setValue(currentPageUnsaved.content);
            removeOfflinePage(_pageID);
            // recheck from checkUnsavedPages
            checkUnsavedPages(_spaceID, _pageID, responseContentUpdatedAt);
          }
        }
      }
    },
    [setValue]
  );

  const saveTransclusionReference = useCallback(async () => {
    const currentSpace = getCurrentSpace();
    const currentEditor = getEditor();

    if (currentSpace && currentEditor) {
      const transclusionReference = currentEditor.store.getState().transclusions;

      const customizationUpdateRequests: ICustomizationUpdateRequest[] = [
        {
          customization_path: ['transclusion_reference'],
          customization_value: transclusionReference,
        },
      ];
      return useUpdateSpaceCustomizationPartialMutation.run([
        { spaceID, customizationUpdateRequests },
      ]);
    }

    return undefined;
  }, [getCurrentSpace, getEditor, spaceID]);

  const saveItemPageData = useCallback(
    (_schema: { [key: string]: string }, _schemaOrder: string[]) => {
      const currentPage = getCurrentPage();

      if (currentPage?.customization?.item_page_data && _schema && _schemaOrder) {
        useUpdatePageCustomizationMutation
          .run([
            {
              spaceID,
              pageID,
              customizationUpdateRequests: [
                {
                  customization_path: ['item_page_data', 'contentSchema'],
                  customization_value: _schema,
                },
                {
                  customization_path: ['item_page_data', 'contentSchemaOrder'],
                  customization_value: _schemaOrder,
                },
              ],
            },
          ])
          .unwrap()
          .catch((error) => {
            toast.error(errToString(error));
          });
      }
    },
    [getCurrentPage, pageID, spaceID]
  );

  const getLocalPage = useCallback(
    async (_spaceID: string, _pageID: string) => {
      const resp = await useGetPageQuery.run([_spaceID, _pageID]).unwrap();
      setvaluePageID(resp?.id);
      if (resp?.content) {
        setValue(resp.content);
        setInitialValue(resp.content);
        setTimeout(() => getEditor()?.synchronizeTransclusion(), 100);
      } else {
        setInitialValue([...initialValueEmpty] as DreamValue);
      }
      // if (resp?.customization?.collection_data) {
      //   setTimeout(
      //     () => getEditor()?.synchronizeCollection(resp?.customization?.collection_data),
      //     100
      //   );
      // }
      if (resp?.customization?.collection_data) {
        getEditor()?.store.getState().setPageCollectionData(resp?.customization?.collection_data);
      }

      checkUnsavedPages(_spaceID, _pageID, resp?.content_updated_at);
    },
    [checkUnsavedPages, getEditor, setValue]
  );

  const getLocalSpace = useCallback(
    async (_spaceID: string) => {
      const resp = await useGetSpaceQuery.run([_spaceID]).unwrap();
      if (resp?.navbar) {
        setNavbar(resp.navbar);
      }
      if (resp?.customization?.transclusion_reference) {
        getEditor()?.store.getState().setTransclusions(resp?.customization?.transclusion_reference);
      }
      if (resp?.customization?.form_reference) {
        getEditor()?.store.getState().setFormReferences(resp?.customization?.form_reference);
      }
      if (resp?.customization?.collection_item_data) {
        getEditor()
          ?.store.getState()
          .setSpaceCollectionItemData(resp?.customization?.collection_item_data);
      }
      if (resp?.routes) {
        getEditor()?.store.getState().setSpaceRoutes(space?.routes);
      }
    },
    [getEditor, setNavbar]
  );

  const prevSpaceID = useRef<string>();
  const prevPageID = useRef<string>();

  useEffect(() => {
    if (!pageID || !spaceID || !getEditor()) {
      setloading(false);
      loadingRef.current = false;
      return;
    }

    const getSpaceAndPage = async () => {
      setloading(true);
      loadingRef.current = true;

      try {
        if (spaceID !== prevSpaceID.current) {
          setNavbar(null);
          getEditor()?.store.getState().setTransclusions({});
          getEditor()?.store.getState().setFormReferences({});
          getEditor()?.store.getState().setSpaceCollectionItemData({});
          getEditor()?.store.getState().setPageCollectionData({});
          getEditor()?.store.getState().setSpaceRoutes(undefined);
          await getLocalSpace(spaceID);
        }

        setValue([...initialValueEmpty] as DreamValue);
        await getLocalPage(spaceID, pageID);
      } catch (error) {
        toast.error(errToString(error));
      } finally {
        prevSpaceID.current = spaceID;
        prevPageID.current = pageID;

        setloading(false);
        loadingRef.current = false;
      }
    };

    getSpaceAndPage();
  }, [spaceID, pageID, getEditor, setValue, getLocalPage, setNavbar, getLocalSpace]);

  // This ref is used to track saveAll call. When there is a pending saveAll,
  // the next saveAll invokation will returns early without further API request.
  // This is to prevent "Use My Changes" pop up keeps prompting.
  const isPendingSaveAllRef = useRef(false);

  const saveAll = useCallback(
    async (options?: {
      shouldSaveContent?: boolean;
      forceSaveContent?: boolean;
      shouldSaveNavbar?: boolean;
      forceSaveNavbar?: boolean;
      shouldSaveTransclusionReference?: boolean;
      shouldSaveItemPageData?: boolean;
    }) => {
      const {
        shouldSaveContent,
        forceSaveContent,
        shouldSaveNavbar,
        forceSaveNavbar,
        shouldSaveTransclusionReference,
        shouldSaveItemPageData,
      } = options || {};

      // pages API
      const savePages = async () => {
        const currentCollectionData = getCurrentPage()?.customization?.collection_data;
        if (currentCollectionData) {
          // prune the collection item data
          const prunedCollectionData = pruneUnusedCollectionData(
            currentCollectionData,
            getCurrentValue()
          );

          // check if the keys of collection data changed
          const collectionDataKeysChanged = !isEqual(
            Object.keys(currentCollectionData),
            Object.keys(prunedCollectionData)
          );

          if (collectionDataKeysChanged) {
            // update page collection data
            useUpdatePageCustomizationMutation.run([
              {
                spaceID,
                pageID,
                customizationUpdateRequests: [
                  {
                    customization_path: ['collection_data'],
                    customization_value: prunedCollectionData,
                  },
                ],
              },
            ]);
          }
        }

        if (shouldSaveContent) {
          await saveContent({
            spaceID,
            pageID,
            newContent: getCurrentValue(),
            force: forceSaveContent,
          });
        }

        if (shouldSaveItemPageData) {
          await saveItemPageData(schemaRef.current, schemaOrderRef.current);
        }

        if (shouldSaveContent || shouldSaveItemPageData) {
          await useGetPageQuery.run([spaceID, pageID]);
        }
      };

      // spaces API
      const saveSpaces = async () => {
        if (shouldSaveNavbar) {
          await saveNavbar({
            spaceID,
            newNavbar: navbar,
            force: forceSaveNavbar,
          });
        }

        if (shouldSaveTransclusionReference) {
          await saveTransclusionReference();
        }

        if (shouldSaveNavbar || shouldSaveTransclusionReference) {
          await useGetSpaceQuery.run([spaceID]);
        }
      };

      if (isPendingSaveAllRef.current) {
        return;
      }

      isPendingSaveAllRef.current = true;

      // pages API and spaces API doesn't need to wait for each other
      // so we can use Promise.all to save them in parallel
      // but each requests inside savePages or saveSpaces need to run in sequence
      await Promise.all([savePages(), saveSpaces()]);

      isPendingSaveAllRef.current = false;
    },
    [
      getCurrentValue,
      navbar,
      pageID,
      saveContent,
      saveItemPageData,
      saveNavbar,
      saveTransclusionReference,
      spaceID,
    ]
  );

  const savePageAndNavbarAndTransclusionReference = useCallback(
    async (force = false) => {
      if (!editable) return;

      const shouldSaveContent =
        (force || (isDraft && !isErrorSaveContent)) &&
        !getCurrentPageIsLoading() &&
        !loadingRef.current &&
        valuePageID === pageID;

      const shouldSaveSpace =
        (force || (isDraft && !errorSaveNavbar && !errSpace)) &&
        !getCurrentSpaceIsLoading() &&
        !loadingRef.current;

      await saveAll({
        shouldSaveContent,
        forceSaveContent: force,
        shouldSaveNavbar: shouldSaveSpace,
        forceSaveNavbar: force,
        shouldSaveTransclusionReference: shouldSaveSpace,
      });
    },
    [
      editable,
      errSpace,
      errorSaveNavbar,
      getCurrentPageIsLoading,
      getCurrentSpaceIsLoading,
      isDraft,
      isErrorSaveContent,
      pageID,
      saveAll,
      valuePageID,
    ]
  );

  useInterval(savePageAndNavbarAndTransclusionReference, SAVE_INTERVAL * 1000);

  useEffect(() => {
    if (!space || !editable) {
      return () => {
        /* do nothing */
      };
    }

    const warningText = 'You have unsaved changes - are you sure you wish to leave this page?';

    function confirmExit() {
      if (isDraft) return warningText;
      return null;
    }

    async function onRouteChangeStart() {
      const value = getCurrentValue();

      const shouldSave =
        pageID &&
        pageID === valuePageID &&
        isDraft &&
        !isEqual(value, initialValueEmpty) &&
        !getCurrentSpaceIsLoading() &&
        !getCurrentPageIsLoading();

      saveAll({
        shouldSaveContent: shouldSave,
        forceSaveContent: true,
        shouldSaveNavbar: shouldSave,
        shouldSaveTransclusionReference: shouldSave,
        shouldSaveItemPageData: shouldSave,
      });

      // if page is not yet backed up, back up now
      if (!getLastBackupHash(slug as string, spaceID, pageID)) {
        saveBackup();
      }
    }

    function handleSaveKeyboard(event: KeyboardEvent) {
      if (pageID && spaceID) {
        if (isSaveHotkey(event)) {
          event.preventDefault();

          const value = getCurrentValue();

          const shouldSaveContent =
            value && !loadingRef.current && !getCurrentPageIsLoading() && valuePageID === pageID;

          const shouldSaveNavbar = !loadingRef.current && !getCurrentSpaceIsLoading();

          saveAll({
            shouldSaveContent,
            shouldSaveNavbar,
            shouldSaveTransclusionReference: true,
            shouldSaveItemPageData: true,
          });
        }
      }
    }

    window.addEventListener('beforeunload', confirmExit);
    document.addEventListener('keydown', handleSaveKeyboard);
    router.events.on('routeChangeStart', onRouteChangeStart);

    return () => {
      window.removeEventListener('beforeunload', confirmExit);
      document.removeEventListener('keydown', handleSaveKeyboard);
      router.events.off('routeChangeStart', onRouteChangeStart);
    };
  }, [
    spaceID,
    space,
    pageID,
    valuePageID,
    navbar,
    isDraft,
    editable,
    router.events,
    getCurrentValue,
    getCurrentSpaceIsLoading,
    getCurrentPageIsLoading,
    slug,
    getEditor,
    saveContent,
    saveNavbar,
    saveTransclusionReference,
    saveItemPageData,
    saveBackup,
    saveAll,
  ]);

  return (
    <TextEditorContext.Provider
      value={useMemo(
        () => ({
          initialValue,
          setInitialValue,
          isDraft,
          loading,
          save: savePageAndNavbarAndTransclusionReference,
        }),
        [initialValue, isDraft, loading, savePageAndNavbarAndTransclusionReference]
      )}
    >
      <UnsavedPagesContext.Provider
        value={useMemo(
          () => ({
            unsavedPages,
            setunsavedPages,
            openUnsavedPages,
            setopenUnsavedPages,
          }),
          [openUnsavedPages, unsavedPages]
        )}
      >
        <UnsavedPagesPopup
          open={openUnsavedPages}
          setopen={setopenUnsavedPages}
          unsavedPages={unsavedPages}
          setunsavedPages={setunsavedPages}
        />
        {children}
      </UnsavedPagesContext.Provider>
    </TextEditorContext.Provider>
  );
}

export function TextEditorProvider({
  editorID,
  children,
}: {
  editorID: string;
  children: ReactNode;
}) {
  return (
    <NavbarEditorProvider>
      <DreamProvider id={editorID}>
        <BaseTextEditorProvider editorID={editorID}>{children}</BaseTextEditorProvider>
      </DreamProvider>
    </NavbarEditorProvider>
  );
}
